import { Component, OnDestroy, OnInit } from '@angular/core';
import { PersonalizationService } from './services/personalization.service';
import { environment } from '../environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { ErrorNavigationService } from './modules/errors/error-500/error-navigation.service';
import { filter, Subscription } from 'rxjs';
import { UserService } from './core/user/user.service';
import { OAuthService } from './core/auth/oauth.service';
import { NwcTerminologyService } from './core/transloco/nwc-terminology.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen/splash-screen.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  /**
   * Constructor
   */
  constructor(
    public _persona: PersonalizationService,
    private router: Router,
    private errorNavigationService: ErrorNavigationService,
    private userService: UserService,
    private oauth: OAuthService,
    private nwcTerminologyService: NwcTerminologyService,
    private _splashScreen: FuseSplashScreenService,
  ) {}

  subscription: Subscription;

  async ngOnInit() {
    this.nwcTerminologyService.start();
    try {
      // Pre-load current user at app start to warm the cache for AuthGuard.
      // PlatformAccessGuard handles the access check before any route activates —
      // duplicating that check here with router.navigate() interferes with the
      // routing lifecycle and prevents NavigationEnd from firing (splash screen never hides).
      if (this.oauth.isSignedIn()) {
        await this.userService.getCurrentUser();
      }

      // pre-load current user at app. start
      const user = await this.userService.getCurrentUser();
      (
        window as Window & { applyNwcSplashBranding?: (user?: unknown) => void }
      ).applyNwcSplashBranding?.(user);
      this.nwcTerminologyService.applyForCurrentUser();
    } finally {
      // Only reveal the app once we know for certain whether to brand it as
      // NWC or WhiteHelmet, so the splash screen never hides on the wrong
      // logo (e.g. on a user's very first login, before any branding data
      // was ever cached locally).
      this._splashScreen.markBrandingResolved();
    }

    this.subscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        const url = event.urlAfterRedirects;
        if (url === '/500') {
          this.errorNavigationService.markRedirectToErrorPage();
        } else {
          this.errorNavigationService.setPreviousUrl(url);
        }
      });

    this.loadHeapScript();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  loadHeapScript() {
    // Only inject in production
    if (environment.production) {
      const scriptId = 'heap-analytics-script';
      if (document.getElementById(scriptId)) return; // Prevent multiple injections

      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'text/javascript';
      script.async = true;
      script.innerHTML = `
        (window.heapReadyCb = window.heapReadyCb || []),
        (window.heap = window.heap || []),
        (heap.load = function (e, t) {
          (window.heap.envId = e),
          (window.heap.clientConfig = t = t || {}),
          (window.heap.clientConfig.shouldFetchServerConfig = !1);

          var a = document.createElement('script');
          a.type = 'text/javascript';
          a.async = !0;
          a.src = 'https://cdn.us.heap-api.com/config/' + e + '/heap_config.js';
          var r = document.getElementsByTagName('script')[0];
          r.parentNode.insertBefore(a, r);

          var n = ['init', 'startTracking', 'stopTracking', 'track', 'resetIdentity',
            'identify', 'getSessionId', 'getUserId', 'getIdentity', 'addUserProperties',
            'addEventProperties', 'removeEventProperty', 'clearEventProperties', 'addAccountProperties',
            'addAdapter', 'addTransformer', 'addTransformerFn', 'onReady', 'addPageviewProperties',
            'removePageviewProperty', 'clearPageviewProperties', 'trackPageview'];

          var i = function (e) {
            return function () {
              var t = Array.prototype.slice.call(arguments, 0);
              window.heapReadyCb.push({
                name: e,
                fn: function () {
                  heap[e] && heap[e].apply(heap, t);
                }
              });
            };
          };

          for (var p = 0; p < n.length; p++) heap[n[p]] = i(n[p]);
        });
        heap.load('871892316');
      `;

      document.head.appendChild(script);
    }
  }
}
